// @flow
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EventActions } from 'modules/event/actions';
import { EventSelector } from 'modules/event/selector';
import { isEmpty } from 'lodash';
import TicketMetaDescription from 'components/molecules/ticketMetaDescription';
import TicketSelectionBox from 'components/organisms/ticketSelectionBox';
import EventTicketGrid from 'components/molecules/eventTicketGrid';
import { CallToAction } from 'components/atoms/callToAction';
import EventTicketCart from 'components/organisms/ticketCart';
import { useViewport } from 'components/templates/viewportProvider';
import { FnBSelection } from '../fnbSelection';
//eslint-disable-next-line
import styles from "./styles.scss";

type Props = {
  event_details: Object,
  selectedTableDetails: Object,
  standingTickets: number,
  manageTableTickets: () => void,
};

const EventTicketSelection = (props: Props) => {
  const {
    event_details,
    selectedTableDetails,
    standingTickets,
    manageTableTickets,
    manageStandingTicketCount,
    onContactUsClick,
    placeholder,
    value,
    onSelect,
    extractDates,
    updateOverlay
  } = props;

  const {
    availableStandingTickets,
    availableTables,
    seating_ticket_price,
    standing_ticket_price,
    table_seating,
    fnbPackages,
    outlet_id
  } = event_details;

  const { tableStrength, tableIds = [] } = selectedTableDetails;
  const [showCart, setShowCart] = useState(false);
  const [isTableSelected, setIsTableSelected] = useState(tableIds.length > 0);
  const { isMobile } = useViewport();
  const containerRef = useRef(null);
  const [showFnB, setShowFnB] = useState(false);
  const [selectedFnBPackages, setSelectedFnBPackages] = useState({});
  const [selectedZoneInfo, setSelectedZoneInfo] = useState(null);

  const scrollToTop = () => {
    if (!isMobile) return;

    if (containerRef.current) {
      containerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };
  
  useEffect(() => {
    if (isMobile) {

      const timer = setTimeout(() => {
        scrollToTop();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [showCart, showFnB, isMobile]);

  useEffect(() => {
    setSelectedZoneInfo(null);
  }, [tableStrength]);

  useEffect(() => {
    // called when component is unmounted to clear all the redux data.
    return function cleanUp() {
      manageStandingTicketCount(0);
      manageTableTickets({ tableStrength: 0, tableIds: [] });
      iterateTableGrid(table_seating);
      setShowFnB(false);
      setSelectedFnBPackages({});
      setShowCart(false);
      setSelectedZoneInfo(null);
    };
  }, [updateOverlay]);

  const getTicketType = (eventDetails) => {
    const restaurantName = eventDetails.venue_name;
    if((restaurantName === 'Terrace') || (restaurantName === 'Windmills BIAL') || (restaurantName === 'Oota,Whitefield')) {
      return 'Entry';
    }
    return 'Standing';
  }

  let standing_tickets_data = {
    ticket_type: getTicketType(event_details),
    ticket_type_id: '1',
    available_tickets: availableStandingTickets,
    price: standing_ticket_price,
    selected: standingTickets,
    total_cost: (standing_ticket_price > 499) ? 1.18 * (standing_ticket_price * standingTickets) 
                  : standing_ticket_price * standingTickets,
  };

  let seating_tickets_data = {
    ticket_type: 'Seating',
    ticket_type_id: '2',
    price: selectedZoneInfo?.zonePrice || 0,
    selected: isTableSelected ? tableStrength : 0,
    total_cost: isTableSelected
      ? ((selectedZoneInfo?.zonePrice || 0) > 499
          ? 1.18 * (selectedZoneInfo.zonePrice * tableStrength)
          : (selectedZoneInfo?.zonePrice || 0) * tableStrength)
      : 0,
    selectedTableIDs: isTableSelected ? tableIds : [],
  };

  let temp_cart_data = { standing_tickets_data, seating_tickets_data };

  const onTableCapacitySelect = tableCapacity => {
    //Used when user changes the table strength from dropdown.
    manageTableTickets({ tableStrength: tableCapacity, tableIds: [] });
    setIsTableSelected(false);
    iterateTableGrid(table_seating);
  };

  const resetTableCapacity = () => {
    manageStandingTicketCount(0);
    manageTableTickets({ tableStrength: 0, tableIds: [] });
    setIsTableSelected(false);
    iterateTableGrid(table_seating);
  };

  const handleSkipFnB = () => {
    setSelectedFnBPackages({});
    setShowFnB(false);
    setShowCart(true);
  }

  const handleFnBNext = () => {
    setShowFnB(false);
    setShowCart(true);
  }

  const renderFnBActionButtons = () => {
    const hasSelectedPackages = Object.keys(selectedFnBPackages).length > 0;
    
    return (
      <div className='fnb-action-buttons'>
        <button
          type='button'
          className='skip-btn'
          onClick={() => {
            setSelectedFnBPackages({});
            setShowFnB(false);
            setShowCart(true);
          }}
        >
          Skip
        </button>
        {hasSelectedPackages &&
          <CallToAction
          text={'Next'}
          onClick={() => handleFnBNext()}
          className='fnb-next-btn'
          disabled={!hasSelectedPackages}
        />
        } 
        
      </div>
    );
  };

  const minSeatingPrice = (Array.isArray(seating_ticket_price) && !isEmpty(seating_ticket_price)) 
    ? seating_ticket_price.reduce((sum, curr) => Math.min(sum, curr.price), +Infinity) : 0;

  const showTicketDetails = () => (
    <>
      {(!isEmpty(table_seating) && minSeatingPrice > 0) && (
        <TicketSelectionBox
          data={seating_tickets_data}
          type={'sitting'}
          className={'sitting-box'}
          onDropDownClick={capacity => onTableCapacitySelect(capacity)}
          availability={availableTables}
          selectedZoneInfo={selectedZoneInfo}
          outlet_id={outlet_id}
          hasSingleSeats={!isEmpty(table_seating.singleTables)}
        />
      )}
      {standing_tickets_data.price > 0 && (
        <TicketSelectionBox
          data={standing_tickets_data}
          type={'standing'}
          className={'standing-box'}
          availability={availableStandingTickets}
          outlet_id={outlet_id}
        />
      )}
      {!isMobile && !showCart && !showFnB && (
        <div className={'desktop-goto-cart'}>{renderAddToCartInfo()}</div>
      )}
    </>
  );

  const renderAddToCartInfo = () => (
    <>
      {(tableIds.length > 0 || standingTickets > 0) && (
        <CallToAction
          text={'Next'}
          onClick={() => fnbPackages.length > 0 ? setShowFnB(true) : setShowCart(true)}
          className={'add-to-cart'}
        />
      )}
      <div className={'contact-info'}>
        <h3 className={'bulk-booking-text'}>
          For Group bookings with more than 8 Guests, please{' '}
        </h3>
        <a href={`#contact`} onClick={onContactUsClick}>
          <h3 className={'contact-us-text'}> contact us</h3>
        </a>
      </div>
      <br />
      <br />
      <br />
    </>
  );

  const iterateTableGrid = table_seating => {
    if (!isEmpty(table_seating)) {
      const allRows = [
        table_seating.firstRow,
        table_seating.secondRow,
        table_seating.thirdRow,
        table_seating.fourthRow,
        table_seating.fifthRow,
        table_seating.sixthRow,
        table_seating.seventhRow,
        table_seating.cornerRow,
        table_seating.sideTables,
        table_seating.singleTables,
      ].filter(Boolean); // Filter out undefined rows
    
      allRows.forEach(row_data => {
        if (Array.isArray(row_data)) {
          row_data.forEach(table => {
            table.imageSelected = false;
          });
        }
      });
    }
  };

  return (
    <Row
      ref={containerRef}
      className={`${
        isMobile ? 'mobile-container' : ''
      } ticket-selection-overlay`}
    >
      <Col sm={12} xs={12} md={6} lg={6} className={'left-section'}>
        <TicketMetaDescription
          data={event_details}
          onSelect={onSelect}
          placeholder={placeholder}
          value={value}
          showCart={showCart}
          showFnB={showFnB}
          extractDates={extractDates}
          resetTableCapacity={resetTableCapacity}
        />
        {!showCart && !showFnB && showTicketDetails()}
        {showFnB && !showCart && (
          <FnBSelection 
            fnbPackages={fnbPackages}
            onSkip={handleSkipFnB}
            onNext={handleFnBNext}
            selectedPackages={selectedFnBPackages}
            onPackageChange={setSelectedFnBPackages}
          />
        )}
        {!isMobile && showFnB && !showCart && renderFnBActionButtons()} 
      </Col>
      {/*Used to show Ticket Table Layout based on boolean condition.*/}
      <Col sm={12} xs={12} md={6} lg={6} className={'right-section'}>
        {!showCart && !isEmpty(table_seating) && (
          <EventTicketGrid
            personCount={tableStrength}
            gridLayout={table_seating}
            tableSelected={tableId => {
              manageTableTickets({
                tableStrength: tableStrength,
                tableIds: tableId,
              });
              setIsTableSelected(tableId.length > 0);
            }}
            eventDate={event_details.event_time?.concert_date}
            showFnB={showFnB}
            seating_ticket_price={seating_ticket_price}
            onZoneSelect={setSelectedZoneInfo}
          />
        )}
        {/*Used to show ticket cart with all details based on boolean condition.*/}
        {showCart && (
          <EventTicketCart
            onEditClick={() => (setShowCart(false), setShowFnB(false), setSelectedFnBPackages({}), resetTableCapacity())}
            data={temp_cart_data}
            eventTimeID={event_details.event_time?.id}
            onClosePress={onContactUsClick}
            eventTitle={event_details.title}
            eventDate={event_details.event_time?.concert_date}
            selectedFnBPackages={selectedFnBPackages}
            outletSeating={table_seating}
            outlet_id={outlet_id}
          />
        )}
        {isMobile && showFnB && !showCart && renderFnBActionButtons()}
      </Col>
      {isMobile && !showFnB && !showCart && (
        <div className={'mobile-goto-cart'}>{renderAddToCartInfo()}</div>
      )}
    </Row>
  );
};

const mapStateToProps = state => {
  return {
    standingTickets: EventSelector.getStandingTickets(state),
    selectedTableDetails: EventSelector.getTableSelected(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { manageTableTickets, manageStandingTicketCount } = EventActions;
  return bindActionCreators(
    {
      manageTableTickets,
      manageStandingTicketCount,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventTicketSelection);
